import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { useInvalidateQuery } from '@/hooks';
import type { z } from 'zod';
import { queryKeyMap } from '../../../../_utils/query-keys';
import {
  PaymentGroupFormSchema,
  PaymentGroupModel,
  PaymentGroupsModel,
} from '../../../_models/payment-group';

const PaymentGroupsModelElement = PaymentGroupsModel.element;

export const usePaymentGroups = <
  T = Array<z.infer<typeof PaymentGroupsModelElement>>,
>({
  projectId,
  select,
  enabled = true,
}: {
  projectId: string;
  select?: (data: z.infer<typeof PaymentGroupsModel>) => T;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: queryKeyMap.paymentGroups(projectId),
    queryFn: async () => {
      const paymentGroups = await axiosGet(
        `/api/projects/${projectId}/payment-groups/`,
      );
      return PaymentGroupsModel.parse(paymentGroups);
    },
    select,
    enabled: !!projectId && enabled,
  });
};

export const usePaymentGroupOptions = (projectId: string) => {
  return usePaymentGroups({
    projectId,
    select: (data) =>
      data.map((d) => ({ value: d.id, label: `${d.displayCode}. ${d.name}` })),
  });
};

export const useCreatePaymentGroup = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async ({
      projectId,
      ...values
    }: z.infer<typeof PaymentGroupFormSchema> & { projectId: string }) => {
      const payload = PaymentGroupFormSchema.parse(values);
      const res = await axiosPost(
        `/api/projects/${projectId}/payment-groups/`,
        // TODO: status should be automatically calculated in the backend
        { ...payload, status: 'new' },
      );
      await Promise.all([
        invalidate(queryKeyMap.paymentGroups(projectId)),
        invalidate(queryKeyMap.paymentGraphData(projectId)),
      ]);
      return PaymentGroupModel.pick({ id: true }).parse(res);
    },
  });
};

export const useUpdatePaymentGroup = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async ({
      id,
      projectId,
      ...values
    }: z.infer<typeof PaymentGroupFormSchema> & {
      id: string;
      projectId: string;
    }) => {
      const payload = PaymentGroupFormSchema.parse(values);
      const res = await axiosPatch(
        `/api/projects/${projectId}/payment-groups/${id}/`,
        payload,
      );
      await Promise.all([
        invalidate(queryKeyMap.paymentGroups(projectId)),
        invalidate(queryKeyMap.paymentGraphData(projectId)),
      ]);
      return res;
    },
  });
};

export const useDeletePaymentGroup = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async ({
      id,
      projectId,
    }: {
      id: string;
      projectId: string;
    }) => {
      const res = await axiosDelete(
        `/api/projects/${projectId}/payment-groups/${id}/`,
      );
      await Promise.all([
        invalidate(queryKeyMap.paymentGroups(projectId)),
        invalidate(queryKeyMap.paymentEdges(projectId)),
        invalidate(queryKeyMap.payments()),
        invalidate(queryKeyMap.contractorPayments(projectId)),
        invalidate(queryKeyMap.paymentGraphData(projectId)),
      ]);
      return res;
    },
  });
};
